import { useEffect, useMemo, useRef } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Status, StatusColor, Color } from "../constants";

// Tile source is configurable so production can move off OpenStreetMap's
// public servers (they discourage business use) without a code change.
const TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL ||
  "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION =
  process.env.REACT_APP_MAP_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';

// Ottawa — only used when there is nothing to show
const FALLBACK_CENTER = [45.4215, -75.6972];

// A pin is always its status colour — the same dot colour the status badge
// uses in the list. Overdue is shown with a red ring rather than by replacing
// the colour, so the status stays readable.
const pinColor = (point) => StatusColor[point.status]?.dot || Color.GRAY;

/**
 * Links for the phone's navigation apps.
 *
 * For an exact position we send coordinates. For a town-centre fallback we
 * send the written address instead — the navigation app can often resolve it
 * better than we can, and sending the town centre would misdirect the driver.
 */
function directionLinks(p) {
  const exact = p.precision !== "city";
  const coords = `${p.latitude},${p.longitude}`;
  const written = [p.address, p.city, "Ontario", "Canada"]
    .filter(Boolean)
    .join(", ");
  const target = exact || !p.address ? coords : written;
  const q = encodeURIComponent(target);

  // Apple Maps first — most field staff are on iPhones, where it is always
  // installed. A website cannot detect which other apps a phone has, so these
  // two cover almost everyone.
  return [
    { label: "Apple Maps", url: `https://maps.apple.com/?daddr=${q}` },
    { label: "Google Maps", url: `https://www.google.com/maps/dir/?api=1&destination=${q}` },
  ];
}

const fmtDue = (d) =>
  d
    ? new Date(d + "T00:00:00").toLocaleDateString("en-CA", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "—";

/** Keeps the visible area matched to whatever the filters returned. */
function FitToPoints({ points }) {
  const map = useMap();
  const signature = points.map((p) => p.id).join(",");
  const lastSignature = useRef(null);

  useEffect(() => {
    if (!points.length || signature === lastSignature.current) return;
    lastSignature.current = signature;

    const bounds = points.map((p) => [p.latitude, p.longitude]);
    if (bounds.length === 1) {
      map.setView(bounds[0], 14);
    } else {
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    }
  }, [signature, points, map]);

  return null;
}

/**
 * Key to the pin colours. Only lists what is actually on screen, so filtering
 * to one status does not leave four irrelevant rows behind.
 */
function Legend({ points }) {
  const present = new Set(points.map((p) => p.status));
  const statuses = Object.values(Status).filter((st) => present.has(st));
  const anyOverdue = points.some((p) => p.is_overdue);
  const anyApproximate = points.some((p) => p.precision === "city");

  if (!statuses.length) return null;

  return (
    <div
      className="absolute bottom-4 left-3 bg-white/95 rounded-lg shadow-md
                 border border-gray-200 px-3 py-2.5"
      style={{ zIndex: 1000, pointerEvents: "none" }}
    >
      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
        Status
      </div>
      {statuses.map((st) => (
        <div key={st} className="flex items-center gap-2 mb-1 last:mb-0">
          <span
            className="w-3 h-3 rounded-full flex-shrink-0"
            style={{
              backgroundColor: StatusColor[st]?.dot || Color.GRAY,
              border: "2px solid #FFFFFF",
              boxShadow: "0 0 0 1px rgba(0,0,0,.12)",
            }}
          />
          <span className="text-xs text-gray-700">{st}</span>
        </div>
      ))}

      {(anyOverdue || anyApproximate) && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          {anyOverdue && (
            <div className="flex items-center gap-2 mb-1">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: Color.GRAY,
                  border: `2px solid ${Color.RED}`,
                }}
              />
              <span className="text-xs text-gray-700">Overdue</span>
            </div>
          )}
          {anyApproximate && (
            <div className="flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full flex-shrink-0"
                style={{
                  backgroundColor: "#FFFFFF",
                  border: `2px solid ${Color.GRAY}`,
                }}
              />
              <span className="text-xs text-gray-700">Town centre only</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkOrderMap({ points, missingLocation, onSelect }) {
  // Several orders often share one address, which would stack pins exactly on
  // top of each other. Spread them into a small ring so each stays clickable.
  const spread = useMemo(() => {
    const byPosition = new Map();
    return points.map((p) => {
      const key = `${p.latitude.toFixed(6)},${p.longitude.toFixed(6)}`;
      const seen = byPosition.get(key) || 0;
      byPosition.set(key, seen + 1);
      if (seen === 0) return p;

      const angle = (seen * 2 * Math.PI) / 8;
      const radius = 0.00012 * (1 + Math.floor(seen / 8));
      return {
        ...p,
        latitude: p.latitude + radius * Math.cos(angle),
        longitude: p.longitude + radius * Math.sin(angle),
      };
    });
  }, [points]);

  if (!points.length) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 py-16 text-center">
        <p className="font-medium text-gray-500">Nothing to show on the map</p>
        <p className="text-sm text-gray-400 mt-1">
          {missingLocation > 0
            ? `${missingLocation} order${missingLocation === 1 ? "" : "s"} have no location yet`
            : "Try adjusting your filters"}
        </p>
      </div>
    );
  }

  return (
    <div>
      {missingLocation > 0 && (
        <div
          className="mb-2 px-3 py-2 rounded-lg text-sm"
          style={{ backgroundColor: "#FFF8E1", color: "#8A6A00" }}
        >
          {missingLocation} order{missingLocation === 1 ? " is" : "s are"} not
          shown — their customer has no location yet.
        </div>
      )}

      <div className="relative rounded-xl border border-gray-200 overflow-hidden">
        <Legend points={points} />
        <MapContainer
          center={FALLBACK_CENTER}
          zoom={11}
          scrollWheelZoom
          style={{ height: "70vh", minHeight: 420, width: "100%" }}
        >
          <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
          <FitToPoints points={spread} />

          {spread.map((p) => {
            const color = pinColor(p);
            // A hollow pin means the position is the town centre, not the
            // actual address.
            const approximate = p.precision === "city";
            return (
              <CircleMarker
                key={p.id}
                center={[p.latitude, p.longitude]}
                radius={8}
                pathOptions={{
                  // Red ring = overdue. Otherwise a white ring for solid pins,
                  // or the status colour for hollow (approximate) ones.
                  color: p.is_overdue
                    ? Color.RED
                    : approximate
                      ? color
                      : "#FFFFFF",
                  weight: p.is_overdue || approximate ? 3 : 2,
                  fillColor: approximate ? "#FFFFFF" : color,
                  fillOpacity: approximate ? 0.9 : 0.95,
                }}
              >
                <Popup>
                  <div style={{ minWidth: 215 }}>
                    <div className="font-semibold text-gray-800">
                      {p.customer_name}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {p.task_category}
                    </div>
                    <div className="text-xs text-gray-500">
                      {[p.city, p.route].filter(Boolean).join(" · ")}
                    </div>

                    <div className="mt-2 text-xs">
                      <span
                        className="inline-block px-2 py-0.5 rounded-full font-medium"
                        style={{
                          backgroundColor:
                            StatusColor[p.status]?.bg || "#F3F4F6",
                          color: StatusColor[p.status]?.text || "#666",
                        }}
                      >
                        {p.status}
                      </span>
                      {p.is_overdue && (
                        <span
                          className="ml-1.5 font-medium"
                          style={{ color: Color.RED }}
                        >
                          Overdue
                        </span>
                      )}
                    </div>

                    <div className="text-xs text-gray-500 mt-1.5">
                      Due: {fmtDue(p.desired_completion_date)}
                      {p.assigned_to ? ` · ${p.assigned_to}` : ""}
                    </div>

                    {approximate && (
                      <div className="text-xs mt-1.5" style={{ color: "#B38600" }}>
                        Approximate — town centre only
                      </div>
                    )}

                    <div className="mt-2.5">
                      <div className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                        Directions
                      </div>
                      <div className="flex gap-1">
                        {directionLinks(p).map((d) => (
                          <a
                            key={d.label}
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-1 text-center py-1.5 rounded-lg border
                                       text-[11px] font-semibold no-underline"
                            style={{
                              borderColor: "#DDE1E7",
                              color: Color.NAVY,
                            }}
                          >
                            {d.label}
                          </a>
                        ))}
                      </div>
                    </div>

                    <button
                      onClick={() => onSelect(p.id)}
                      className="mt-2 w-full py-1.5 rounded-lg text-xs font-semibold text-white"
                      style={{ backgroundColor: Color.NAVY }}
                    >
                      Open order
                    </button>
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </MapContainer>
      </div>
    </div>
  );
}
